/**
 * Date utilities to handle timezone-safe date formatting
 * Fixes the issue where dates shift by a day due to timezone conversion
 */

/**
 * Safely formats a date string (YYYY-MM-DD) or Date object for display
 * without timezone conversion issues
 * @param {string|Date} date - Date string in YYYY-MM-DD format or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatDateSafe = (date, options = {}) => {
  if (!date) return 'N/A';
  
  try {
    // Default formatting options
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Force UTC to prevent timezone shifts
    };
    
    const formatOptions = { ...defaultOptions, ...options };
    
    let dateObj;
    
    // Handle different input types
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // YYYY-MM-DD format - parse as UTC to prevent timezone shift
        const [year, month, day] = date.split('-').map(Number);
        dateObj = new Date(Date.UTC(year, month - 1, day));
      } else {
        // Other string formats
        dateObj = new Date(date);
      }
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firebase Timestamp
      dateObj = date.toDate();
    } else {
      return 'Invalid Date';
    }
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    return dateObj.toLocaleDateString('en-US', formatOptions);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date for display with short format (Jan 15, 2024)
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateShort = (date) => {
  return formatDateSafe(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats a date for display with long format (January 15, 2024)
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateLong = (date) => {
  return formatDateSafe(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formats a date for form inputs (YYYY-MM-DD)
 * @param {string|Date} date - Date to format
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  try {
    let dateObj;
    
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in correct format
        return date;
      }
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firebase Timestamp
      dateObj = date.toDate();
    } else {
      return '';
    }
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    // Format as YYYY-MM-DD using local timezone to preserve the date
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('Error formatting date for input:', error);
    return '';
  }
};

/**
 * Safely formats a date/time with both date and time components
 * @param {string|Date} date - Date to format
 * @param {Object} options - Additional formatting options
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  
  return formatDateSafe(date, { ...defaultOptions, ...options });
};

/**
 * Checks if a date string or Date object represents a valid date
 * @param {string|Date} date - Date to validate
 * @returns {boolean} - True if valid date
 */
export const isValidDate = (date) => {
  if (!date) return false;
  
  try {
    let dateObj;
    
    if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return false;
    }
    
    return !isNaN(dateObj.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Gets the current date in YYYY-MM-DD format for form inputs
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export const getCurrentDateForInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Legacy compatibility function - gradually replace with formatDateSafe
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  return formatDateShort(date);
};